"use client";

import { useLingui } from "@lingui/react/macro";
import { ChevronDownIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { Button } from "@/components/ui/button";
import { isHighTierModelId } from "@/lib/ai/model-tier";
import { AVAILABLE_MODELS, type ModelId } from "@/lib/ai/models";
import { cn } from "@/lib/utils";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import { ModelProviderIcon } from "./model-provider-icon";
import { ModelSelectProBadge } from "./model-select-pro-badge";
import { ModelSelectQuotaStatusIndicator } from "./model-select-quota-status-indicator";
import { ModelSelectQuotaWeightBadge } from "./model-select-quota-weight-badge";

type Props = Omit<ComponentProps<typeof Button>, "children"> & {
  model: ModelId;
  quotaState: ChatQuotaTierIndicatorState;
};

function resolveTriggerToneClass(quotaState: ChatQuotaTierIndicatorState) {
  if (quotaState.kind === "hidden") {
    return "text-foreground";
  }

  return "text-foreground";
}

function resolveSelectedModelLabel(model: ModelId) {
  return (
    AVAILABLE_MODELS.find((availableModel) => availableModel.value === model)
      ?.label ?? model
  );
}

export function ModelSelectTrigger({
  model,
  quotaState,
  disabled,
  className,
  ...buttonProps
}: Props) {
  const { t } = useLingui();
  const label = resolveSelectedModelLabel(model);
  const tooltipLabel =
    quotaState.kind === "warning"
      ? t`Near this month's Amethyst AI limit (${quotaState.remainingPercentage}% left).`
      : null;

  const selectedModel = AVAILABLE_MODELS.find(
    (availableModel) => availableModel.value === model,
  );
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      aria-label={t`Select AI model`}
      title={tooltipLabel ?? undefined}
      className={cn(
        "h-8 min-w-0 max-w-[22rem] shrink items-center gap-1.5 px-2 pr-1.5 font-medium",
        "aria-expanded:bg-accent aria-expanded:text-foreground",
        resolveTriggerToneClass(quotaState),
        className,
      )}
      {...buttonProps}
    >
      {quotaState.kind !== "hidden" ? (
        <ModelSelectQuotaStatusIndicator state={quotaState} />
      ) : null}
      {selectedModel ? (
        <ModelProviderIcon
          provider={selectedModel.provider}
          className="text-muted-foreground"
        />
      ) : null}
      <span className="flex min-w-0 flex-1 items-center leading-none">
        <span className="relative top-px truncate leading-none">{label}</span>
        {isHighTierModelId(model) ? (
          <ModelSelectProBadge className="ml-2" />
        ) : null}
        <ModelSelectQuotaWeightBadge modelId={model} className="ml-2" />
      </span>
      <ChevronDownIcon className="size-3.5 text-muted-foreground" />
    </Button>
  );
}
